import type { StockItem } from '../types/stock'

/**
 * Bestellijst voor de leverancier (docs/VOORRAAD.md). Puur en getest.
 *
 * - Voorraad = webshop + salon samen (overboeken kan altijd); een plek die niet bijgehouden wordt telt niet.
 * - Verkoop per dag = netto verkocht in de laatste 30 dagen (beide kanalen) ÷ 30.
 * - Voorstel = genoeg voor `coverDays` dagen verkoop, min wat er ligt; nooit minder dan 0.
 * - Verbruiksartikelen worden niet verkocht: die staan erop als ze op zijn (1 stuk), de rest kiest Sarah.
 * - Een artikel zonder enige bijgehouden plek staat er nooit op: daar weet niemand iets van.
 */

/** Zoveel dagen moet een bestelling vooruit: levertijd plus tot de volgende bestelling. */
export const REORDER_COVER_DAYS = 42

export type ReorderLine = {
  itemId: string
  name: string
  /** Leverancier = het merk; zonder merk "Overig". */
  supplier: string
  onHand: number
  /** Verkocht per dag (gemiddeld over 30 dagen). */
  perDay: number
  /** Over hoeveel dagen het op is bij dit tempo; `null` = er wordt niets verkocht. */
  daysLeft: number | null
  suggested: number
  costPriceCents?: number
}

export const OTHER_SUPPLIER = 'Overig'

const tracked = (i: StockItem) => Object.values(i.levels).some((v) => v !== null)
const onHandOf = (i: StockItem) => Object.values(i.levels).reduce<number>((s, v) => s + (v ?? 0), 0)
const soldOf = (i: StockItem) => Object.values(i.sold30).reduce((s, v) => s + Math.max(0, v), 0)

/** Wat er besteld moet worden, per leverancier en daarbinnen wat het eerst op is. */
export function reorderLines(items: readonly StockItem[], coverDays = REORDER_COVER_DAYS): ReorderLine[] {
  return items
    .filter(tracked)
    .map((i): ReorderLine => {
      const onHand = onHandOf(i)
      const perDay = soldOf(i) / 30
      const need = i.kind === 'verbruik' ? (onHand <= 0 ? 1 : 0) : Math.ceil(perDay * coverDays)
      return {
        itemId: i.id,
        name: i.name,
        supplier: i.brand?.trim() || OTHER_SUPPLIER,
        onHand,
        perDay,
        daysLeft: perDay > 0 ? Math.floor(Math.max(0, onHand) / perDay) : null,
        suggested: Math.max(0, need - onHand),
        ...(i.costPriceCents !== undefined ? { costPriceCents: i.costPriceCents } : {}),
      }
    })
    .filter((l) => l.suggested > 0)
    .sort((a, b) => a.supplier.localeCompare(b.supplier, 'nl') || (a.daysLeft ?? Infinity) - (b.daysLeft ?? Infinity) || a.name.localeCompare(b.name, 'nl'))
}

/** Een gekozen aantal: geheel en tussen 0 en 999. */
export const clampOrderQty = (qty: number) => (Number.isFinite(qty) ? Math.min(999, Math.max(0, Math.round(qty))) : 0)

/** Inkoopwaarde excl. btw; artikelen zonder inkoopprijs tellen niet mee (`unknown` = hoeveel). */
export function orderCost(lines: readonly { qty: number; costPriceCents?: number }[]) {
  return lines.reduce(
    (t, l) => (l.qty <= 0 ? t : l.costPriceCents === undefined ? { ...t, unknown: t.unknown + 1 } : { ...t, cents: t.cents + l.qty * l.costPriceCents }),
    { cents: 0, unknown: 0 },
  )
}

/** De tekst voor een mail aan de leverancier: per leverancier een kopje, per regel "3× Naam". Aantal 0 valt weg. */
export function orderText(lines: readonly { supplier: string; name: string; qty: number }[]): string {
  const bySupplier = new Map<string, string[]>()
  for (const l of lines) {
    if (l.qty <= 0) continue
    bySupplier.set(l.supplier, [...(bySupplier.get(l.supplier) ?? []), `${l.qty}× ${l.name}`])
  }
  return [...bySupplier].map(([supplier, rows]) => [supplier, ...rows].join('\n')).join('\n\n')
}
